import type { Vector } from '@ardoq/graph';

import {
  BlocksReference,
  BlocksNode,
  BlocksPlacementStyle,
  BlocksGraph,
} from '../types';
import { getBlockGrid } from './blockGrid';
import { getColPositions, getRowPositions } from './util';
import {
  getLCA,
  getOpenAscendant,
  isAscendancyOpen,
  isAscendant,
  isNodeOpen,
} from '../util';
import { Router, RoutingChannel, RoutingInfo, Waypoint } from './types';
import { IntervalTree } from 'tabview/visualizations/misc/intervaltree';
import type { BusSegment } from './getbusid';
import {
  routeAscendant,
  routeDescendant,
  routeSelf,
  routeSiblings,
} from './route';
import { ArrowType } from '@ardoq/api-types';
import { AQBLOCKS_ROUTING_ENDSEGMENT_SIZE } from '../consts';
import { SpanConstraint } from 'tabview/visualizations/aq-blocks/placeandroute/spansizes';

export const createRouter = (
  graph: BlocksGraph,
  getBusId: (e: BlocksReference, segment: BusSegment) => string,
  placementStyle: BlocksPlacementStyle
): Router => {
  const { routingPad, routingSep } = placementStyle;

  const createRoutingInfo = (node: BlocksNode): RoutingInfo => {
    const colChannels: RoutingChannel[] = [];
    const rowChannels: RoutingChannel[] = [];

    /* assign a microchannel from the specified channel index in the set channels */
    const assignMicroChannel = (
      link: BlocksReference,
      channels: RoutingChannel[],
      index: number,
      range: [number, number],
      segment: BusSegment
    ): number => {
      if (range[0] > range[1]) {
        const t = range[0];

        range[0] = range[1];
        range[1] = t;
      }

      while (channels.length <= index) {
        channels.push([]);
      }

      const channel = channels[index];
      /* Which ends the segment touches decides whether it may share a lane with its siblings. */
      const busid = getBusId(link, segment);

      for (let id = 0; id < channel.length; ++id) {
        if (channel[id].tryAdd(range[0], range[1], busid)) {
          /* managed to add the range to the μchannel; call it good */

          return id;
        }
      }

      /* failed to add the reange to an existing μchannel; create a new one */

      channel.push(new IntervalTree<string>());
      channel[channel.length - 1].add(range[0], range[1], busid);

      return channel.length - 1;
    };

    const assignMicroCol = (
      link: BlocksReference,
      col: number,
      rows: [number, number],
      segment: BusSegment
    ) => {
      return assignMicroChannel(link, colChannels, col, rows, segment);
    };

    const assignMicroRow = (
      link: BlocksReference,
      row: number,
      cols: [number, number],
      segment: BusSegment
    ) => {
      return assignMicroChannel(link, rowChannels, row, cols, segment);
    };

    return {
      assignMicroCol: assignMicroCol,
      assignMicroRow: assignMicroRow,
      colChannels: colChannels,
      rowChannels: rowChannels,
      blockGrid: getBlockGrid(node),
    };
  };

  const root = graph.root;
  const routingInfo = createRoutingInfo(root);
  const waypoints = new Map<BlocksReference, Waypoint[]>();

  const calcWaypoints = (link: BlocksReference): Waypoint[] => {
    link.sourceProxy = undefined;
    link.targetProxy = undefined;
    link.bounds = undefined;

    const lca = getLCA(link.source, link.target);

    if (!lca || !isNodeOpen(lca) || !isAscendancyOpen(lca)) {
      /* invisible reference */
      return [];
    }

    link.sourceProxy = getOpenAscendant(link.source);
    link.targetProxy = getOpenAscendant(link.target);

    if (!link.sourceProxy || !link.targetProxy) {
      return [];
    }

    if (link.sourceProxy === link.targetProxy && link.source !== link.target) {
      /* fake self-reference */
      return [];
    }

    if (link.sourceProxy === link.targetProxy) {
      return routeSelf(link.sourceProxy, link.targetProxy, routingInfo);
    }

    if (link.sourceProxy.parent === lca && link.targetProxy.parent === lca) {
      return routeSiblings(link.sourceProxy, link.targetProxy, routingInfo);
    }

    if (isAscendant(link.targetProxy, link.sourceProxy)) {
      return routeAscendant(link.sourceProxy, link.targetProxy, routingInfo);
    }

    if (isAscendant(link.sourceProxy, link.targetProxy)) {
      return routeDescendant(link.sourceProxy, link.targetProxy, routingInfo);
    }

    return routeSiblings(link.sourceProxy, link.targetProxy, routingInfo);
  };

  const sign = (n: number) => {
    if (n < 0) return -1;
    if (n > 0) return 1;
    return 0;
  };

  const assignMicroChannels = (
    link: BlocksReference,
    waypoints: Waypoint[]
  ) => {
    for (let j = 1; j < waypoints.length; ++j) {
      const wpi = waypoints[j - 1];
      const wpj = waypoints[j];
      const segment: BusSegment = {
        atSource: j === 1,
        atTarget: j === waypoints.length - 1,
      };

      if (wpi.col === wpj.col) {
        const bgn = wpi.row + sign(wpi.μrow);
        const end = wpj.row + sign(wpj.μrow);

        const μcol = routingInfo.assignMicroCol(
          link,
          wpi.col,
          [bgn, end],
          segment
        );
        wpi.μcol = isNaN(wpi.μcol) ? μcol : wpi.μcol;
        wpj.μcol = isNaN(wpj.μcol) ? μcol : wpj.μcol;
      }

      if (wpi.row === wpj.row) {
        const bgn = wpi.col + sign(wpi.μcol);
        const end = wpj.col + sign(wpj.μcol);

        const μrow = routingInfo.assignMicroRow(
          link,
          wpi.row,
          [bgn, end],
          segment
        );
        wpi.μrow = isNaN(wpi.μrow) ? μrow : wpi.μrow;
        wpj.μrow = isNaN(wpj.μrow) ? μrow : wpj.μrow;
      }
    }
  };

  // exported function to calculate the waypoints for a reference
  const calculateWaypoints = (reference: BlocksReference) => {
    const wps = calcWaypoints(reference);

    if (wps.length > 1) {
      assignMicroChannels(reference, wps);

      waypoints.set(reference, wps);
    }

    return wps;
  };

  const buildGridConstraints = (
    graph: BlocksGraph,
    dimension: number,
    constraints: SpanConstraint[]
  ) => {
    const root = graph.root;
    const { colSize, rowSize } = root;

    if (!colSize || !rowSize) {
      return;
    }

    const getRoutingSize = (channel: RoutingChannel | null) => {
      const μchannels = channel ? channel.length : 0;
      const routingSize = μchannels > 0 ? (μchannels - 1) * routingSep : 0;

      return routingSize;
    };

    const buildMarkerConstraint = (wp: Waypoint) => {
      const margin = AQBLOCKS_ROUTING_ENDSEGMENT_SIZE;

      if (dimension === 0 && wp.μcol === -Infinity && wp.col > 0) {
        const channel = routingInfo.colChannels[wp.col - 1];
        const routingSize = getRoutingSize(channel);

        constraints.push({
          first: wp.col - 1,
          last: wp.col - 1,
          minimum: 2 * margin + routingSize,
        });
      }

      if (dimension === 0 && wp.μcol === +Infinity) {
        const channel = routingInfo.colChannels[wp.col + 1];
        const routingSize = getRoutingSize(channel);

        constraints.push({
          first: wp.col + 1,
          last: wp.col + 1,
          minimum: 2 * margin + routingSize,
        });
      }

      if (dimension === 1 && wp.μrow === -Infinity && wp.row > 0) {
        const channel = routingInfo.rowChannels[wp.row - 1];
        const routingSize = getRoutingSize(channel);

        constraints.push({
          first: wp.row - 1,
          last: wp.row - 1,
          minimum: 2 * margin + routingSize,
        });
      }

      if (dimension === 1 && wp.μrow === +Infinity) {
        const channel = routingInfo.rowChannels[wp.row + 1];
        const routingSize = getRoutingSize(channel);

        constraints.push({
          first: wp.row + 1,
          last: wp.row + 1,
          minimum: 2 * margin + routingSize,
        });
      }
    };

    const cnt = dimension === 0 ? colSize.length : rowSize.length;

    // #region size constraints for each routing channel
    for (let i = 0; i < cnt; ++i) {
      const channel =
        dimension === 0
          ? routingInfo.colChannels[i]
          : routingInfo.rowChannels[i];
      const μchannels = channel ? channel.length : 0;
      const routingSize =
        μchannels > 0 ? 2 * routingPad + (μchannels - 1) * routingSep : 0;

      if (routingSize > 0) {
        constraints.push({ first: i, last: i, minimum: routingSize });
      }
    }
    // #endregion

    // #region size constraints for the end segement of each reference
    for (const [reference, wps] of waypoints) {
      if (reference.lineBeginning !== ArrowType.NONE && wps.length > 1) {
        buildMarkerConstraint(wps[0]);
      }

      if (reference.lineEnding !== ArrowType.NONE && wps.length > 1) {
        buildMarkerConstraint(wps[wps.length - 1]);
      }
    }
    // #endregion
  };

  // exported function to create the route from a routed link's waypoints
  const calculateRoute = (reference: BlocksReference) => {
    const { colSize, rowSize } = root;

    if (!colSize || !rowSize) {
      return undefined;
    }

    const linkWaypoints = waypoints.get(reference);

    if (!linkWaypoints || linkWaypoints.length < 2) {
      return undefined;
    }

    const route: Vector[] = [];
    const colPos = getColPositions(root);
    const rowPos = getRowPositions(root);

    for (const waypoint of linkWaypoints) {
      const colWidth = colSize[waypoint.col];
      const colChannel = routingInfo.colChannels[waypoint.col];
      const routingWidth = colChannel
        ? (colChannel.length - 1) * routingSep
        : 0;
      const x =
        waypoint.μcol === -Infinity
          ? colPos[waypoint.col]
          : waypoint.μcol === +Infinity
            ? colPos[waypoint.col] + colWidth
            : colPos[waypoint.col] +
              0.5 * (colWidth - routingWidth) +
              waypoint.μcol * routingSep;

      /* get row stuff and y */

      const rowHeight = rowSize[waypoint.row];
      const rowChannel = routingInfo.rowChannels[waypoint.row];
      const routingHeight = rowChannel
        ? (rowChannel.length - 1) * routingSep
        : 0;

      const y =
        waypoint.μrow === -Infinity
          ? rowPos[waypoint.row]
          : waypoint.μrow === +Infinity
            ? rowPos[waypoint.row] + rowHeight
            : rowPos[waypoint.row] +
              0.5 * (rowHeight - routingHeight) +
              waypoint.μrow * routingSep;

      route.push([x, y]);
    }

    return route;
  };

  return {
    calculateWaypoints: calculateWaypoints,
    buildGridConstraints: buildGridConstraints,
    calculateRoute: calculateRoute,
  };
};
